"use client";

import * as React from "react";
import Drawer from "@mui/material/Drawer";
import { useLocation } from "react-router-dom";
import type { NavItemConfig } from "@/types/nav";
import { useNavigationList } from "../../../api/navigation-hooks";
import { GeneralSideNavigation, DynamicDeviceSideNavigation, } from "./renderNavGroups";

export interface MobileNavProps {
    onClose?: () => void;
    open?: boolean;
    items?: NavItemConfig[];
}

function getDeviceUuidFromPathname(pathname: string): string | undefined {
    return pathname.match(
        /^\/dashboard\/devices\/([^/]+)(?:\/|$)/
    )?.[1];
}

export function MobileNav({ items = [], open, onClose }: MobileNavProps): React.JSX.Element {
    const location = useLocation();

    const currentDeviceUuid = getDeviceUuidFromPathname(location.pathname);

    const { data: deviceNavigation, } = useNavigationList(currentDeviceUuid);

    const isDeviceRoute = currentDeviceUuid !== undefined;

    return (
        <Drawer
            PaperProps={{
                sx: {
                    "--MobileNav-background": "var(--mui-palette-neutral-950)",
                    "--MobileNav-color": "var(--mui-palette-common-white)",
                    "--NavGroup-title-color": "var(--mui-palette-neutral-400)",
                    "--NavItem-color": "var(--mui-palette-neutral-300)",
                    "--NavItem-hover-background": "rgba(255, 255, 255, 0.04)",
                    "--NavItem-active-background": "var(--mui-palette-primary-main)",
                    "--NavItem-active-color": "var(--mui-palette-primary-contrastText)",
                    "--NavItem-disabled-color": "var(--mui-palette-neutral-500)",
                    "--NavItem-icon-color": "var(--mui-palette-neutral-400)",
                    "--NavItem-icon-active-color": "var(--mui-palette-primary-contrastText)",
                    "--NavItem-icon-disabled-color": "var(--mui-palette-neutral-600)",
                    "--NavItem-expand-color": "var(--mui-palette-neutral-400)",
                    "--NavItem-children-border": "var(--mui-palette-neutral-700)",
                    "--NavItem-children-indicator": "var(--mui-palette-neutral-400)",
                    "--Workspaces-background": "var(--mui-palette-neutral-950)",
                    "--Workspaces-border-color": "var(--mui-palette-neutral-700)",
                    "--Workspaces-title-color": "var(--mui-palette-neutral-400)",
                    "--Workspaces-name-color": "var(--mui-palette-neutral-300)",
                    "--Workspaces-expand-color": "var(--mui-palette-neutral-400)",
                    bgcolor: "var(--MobileNav-background)",
                    color: "var(--MobileNav-color)",
                    display: "flex",
                    flexDirection: "column",
                    maxWidth: "100%",
                    width: "350px",
                    zIndex: "var(--MobileNav-zIndex)",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": {
                        display: "none",
                    },
                },
            }}
            onClose={onClose}
            open={open}
        >
            {isDeviceRoute && deviceNavigation ? (
                <DynamicDeviceSideNavigation
                    onClose={onClose}
                    isMobile
                    navigation={deviceNavigation}
                    items={items}
                />
            ) : (
                <GeneralSideNavigation
                    onClose={onClose}
                    isMobile
                />
            )}
        </Drawer>
    );
}
